import { ReactElement, ReactNode, useCallback, useMemo, useState } from "react";
import {
  removeTransactionCartItemFromTransactionCart,
  addTransactionCartItemToTransactionCart,
  getTotalAmountToChargeFromTransactionCart,
  getTransactionCartItemFromTransactionCart,
} from "../../helpers/transactionCartHelper";
import TransactionCart from "../../models/transactionCart/transactionCart";
import TransactionCartItem from "../../models/transactionCart/transactionCartItem";
import { TransactionCartContext } from "./TransactionCartContext";

interface TransactionCartProviderProps {
  children: ReactNode;
}

/*
 * Transaction cart
 */
const TransactionCartProvider = (props: TransactionCartProviderProps): ReactElement => {
  const { children } = props;
  const [transactionItems, setTransactionItems] = useState<TransactionCartItem[]>([]);

  const getTransactionFromCart = useCallback(
    (transactionId: string) => getTransactionCartItemFromTransactionCart(transactionItems, transactionId),
    [transactionItems]
  );

  // Removes an existing transaction from the cart
  const removeTransactionFromCart = useCallback(
    (transactionId: string) =>
      setTransactionItems((previousItems) => removeTransactionCartItemFromTransactionCart(previousItems, transactionId)),
    []
  );

  // Adds a transaction to the cart
  const addTransactionToCart = useCallback(
    (item: TransactionCartItem) =>
      setTransactionItems((previousItems) => addTransactionCartItemToTransactionCart(previousItems, item)),
    []
  );

  // Updates a transaction in the cart
  const updateTransactionInCart = useCallback(
    (item: TransactionCartItem) =>
      setTransactionItems((previousItems) =>
        addTransactionCartItemToTransactionCart(
          removeTransactionCartItemFromTransactionCart(previousItems, item.transactonId),
          item
        )
      ),
    []
  );

  // Returns the total amount to charge
  const getTotalAmountToCharge = useCallback(
    () => getTotalAmountToChargeFromTransactionCart(transactionItems),
    [transactionItems]
  );

  const getCurrency = useCallback(() => (transactionItems.length > 0 ? transactionItems[0].currency : ""), [transactionItems]);

  const emptyCart = useCallback(() => {
    setTransactionItems([]);
  }, []);

  const context = useMemo<TransactionCart>(
    () => ({
      transactionItems,
      getTransactionFromCart,
      addTransactionToCart,
      removeTransactionFromCart,
      updateTransactionInCart,
      getTotalAmountToCharge,
      getCurrency,
      emptyCart,
    }),
    [
      transactionItems,
      getTransactionFromCart,
      addTransactionToCart,
      removeTransactionFromCart,
      updateTransactionInCart,
      getTotalAmountToCharge,
      getCurrency,
      emptyCart,
    ]
  );

  return <TransactionCartContext.Provider value={context}>{children}</TransactionCartContext.Provider>;
};

export default TransactionCartProvider;
